import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Loader2,
  Eye,
  CheckCircle,
  PenLine,
  UserCheck,
  Shield,
  Lock,
} from 'lucide-react';

/**
 * SignatureModal - 21 CFR Part 11 Compliant Electronic Signature Modal
 */
const SignatureModal = ({
  isOpen,
  onClose,
  selectedDocForWorkflow,
  user,
  signatureText,
  setSignatureText,
  signatureMeaning,
  setSignatureMeaning,
  signaturePassword,
  setSignaturePassword,
  signatureRole,
  setSignatureRole,
  processingSignature,
  handleSignDocument,
}) => {
  const handleClose = (open) => {
    onClose(open);
    if (!open) {
      setSignatureText('');
      setSignatureMeaning('');
      setSignaturePassword('');
      setSignatureRole('');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900 dark:text-slate-100">
            <PenLine className="w-5 h-5 text-teal-400" />
            Electronic Signature
            <span className="text-xs bg-green-500/20 text-green-600 border border-green-500/30 px-2 py-0.5 rounded-full ml-2">
              21 CFR Part 11
            </span>
          </DialogTitle>
          <DialogDescription className="text-slate-600 dark:text-slate-400">
            Sign document: <span className="font-medium text-slate-900 dark:text-slate-100">{selectedDocForWorkflow?.name}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-4 max-h-[65vh] overflow-y-auto">
          {/* Compliance Notice */}
          <div className="p-4 bg-teal-500/20 border border-teal-500/30 rounded-lg text-sm">
            <p className="font-semibold text-teal-300 mb-2 flex items-center gap-2">
              <Shield className="w-4 h-4" />
              21 CFR Part 11 & ISO Compliance
            </p>
            <ul className="text-teal-400 space-y-1 text-xs">
              <li>• Your electronic signature has the same legal weight as a handwritten signature</li>
              <li>• Re-authentication is required to confirm your identity</li>
              <li>• All signatures are timestamped and audit-logged</li>
              <li>• This record cannot be altered after signing</li>
            </ul>
          </div>

          {/* Signature Role Selection */}
          <div className="space-y-2">
            <Label className="text-slate-700 dark:text-slate-300">Your Role for This Signature <span className="text-red-600">*</span></Label>
            <Select value={signatureRole} onValueChange={setSignatureRole}>
              <SelectTrigger className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100">
                <SelectValue placeholder="Select your role" />
              </SelectTrigger>
              <SelectContent className="bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600">
                <SelectItem value="reviewer" className="text-gray-700 hover:bg-slate-200">
                  <span className="flex items-center gap-2">
                    <Eye className="w-3 h-3 text-red-600" /> Reviewer
                  </span>
                </SelectItem>
                <SelectItem value="approver" className="text-gray-700 hover:bg-slate-200">
                  <span className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-purple-600" /> Approver
                  </span>
                </SelectItem>
                <SelectItem value="qc" className="text-gray-700 hover:bg-slate-200">
                  <span className="flex items-center gap-2">
                    <UserCheck className="w-3 h-3 text-amber-600" /> Quality Control (QC)
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Signature Text */}
          <div className="space-y-2">
            <Label className="text-slate-700 dark:text-slate-300">Electronic Signature <span className="text-red-600">*</span></Label>
            <Input
              placeholder="Type: /s/ Your Full Name"
              value={signatureText}
              onChange={(e) => setSignatureText(e.target.value)}
              className="font-serif text-lg h-12 border-2 bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:text-slate-400 focus:border-teal-500"
            />
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Format: /s/ followed by your full legal name (e.g., /s/ John Smith)
            </p>
          </div>

          {/* Signature Meaning */}
          <div className="space-y-2">
            <Label className="text-slate-700 dark:text-slate-300">Signature Meaning <span className="text-red-600">*</span></Label>
            <Select value={signatureMeaning} onValueChange={setSignatureMeaning}>
              <SelectTrigger className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100">
                <SelectValue placeholder="Select what your signature certifies" />
              </SelectTrigger>
              <SelectContent className="bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600">
                <SelectItem value="I have reviewed this document and confirm its accuracy and completeness" className="text-gray-700 hover:bg-slate-200">
                  Reviewed - Confirm accuracy and completeness
                </SelectItem>
                <SelectItem value="I approve this document and authorize it for the next workflow stage" className="text-gray-700 hover:bg-slate-200">
                  Approved - Authorize for next stage
                </SelectItem>
                <SelectItem value="I have completed QC review and verify this document meets all quality and compliance requirements" className="text-gray-700 hover:bg-slate-200">
                  QC Verified - Meets quality/compliance requirements
                </SelectItem>
                <SelectItem value="I acknowledge receipt, review, and understanding of this document" className="text-gray-700 hover:bg-slate-200">
                  Acknowledged - Receipt and understanding
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Password Re-authentication (21 CFR Part 11 requirement) */}
          <div className="space-y-2 p-4 bg-amber-500/20 border border-amber-500/30 rounded-lg">
            <Label className="flex items-center gap-2 text-amber-300">
              <Lock className="w-4 h-4" />
              Password Re-authentication <span className="text-red-600">*</span>
            </Label>
            <Input
              type="password"
              placeholder="Enter your password to confirm identity"
              value={signaturePassword}
              onChange={(e) => setSignaturePassword(e.target.value)}
              className="bg-slate-100 dark:bg-slate-800 border-amber-500/50 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:text-slate-400 focus:border-amber-400"
            />
            <p className="text-xs text-amber-600">
              Per 21 CFR Part 11 §11.200, re-authentication is required for each electronic signature
            </p>
          </div>

          {/* Signer Information */}
          <div className="p-4 bg-slate-100 dark:bg-slate-800/80 rounded-lg text-sm border border-slate-300 dark:border-slate-600">
            <p className="font-semibold text-slate-900 dark:text-slate-100 mb-3">Signature Record Details</p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-slate-500 dark:text-slate-400">Signer Name:</span>
                <p className="font-medium text-slate-900 dark:text-slate-100">{user?.name}</p>
              </div>
              <div>
                <span className="text-slate-500 dark:text-slate-400">Signer Email:</span>
                <p className="font-medium text-slate-900 dark:text-slate-100">{user?.email}</p>
              </div>
              <div>
                <span className="text-slate-500 dark:text-slate-400">Date/Time (UTC):</span>
                <p className="font-medium text-slate-900 dark:text-slate-100">{new Date().toISOString()}</p>
              </div>
              <div>
                <span className="text-slate-500 dark:text-slate-400">Document Version:</span>
                <p className="font-medium text-slate-900 dark:text-slate-100">{selectedDocForWorkflow?.current_version || 1}</p>
              </div>
            </div>
          </div>

          {/* Legal Acknowledgment */}
          <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-xs text-red-300">
            <p className="font-semibold mb-1">Legal Acknowledgment</p>
            <p>By clicking &quot;Sign Document&quot;, I certify that:</p>
            <ul className="mt-1 space-y-0.5 ml-3">
              <li>• I am the person identified above</li>
              <li>• I have reviewed the document in its entirety</li>
              <li>• My electronic signature is legally binding</li>
              <li>• I understand this signature cannot be revoked</li>
            </ul>
          </div>
        </div>
        <DialogFooter className="border-t border-slate-300 dark:border-slate-600 pt-4">
          <Button variant="outline" onClick={() => handleClose(false)} className="rounded-full bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-200 hover:text-slate-900 dark:text-slate-100">
            Cancel
          </Button>
          <Button 
            onClick={handleSignDocument}
            disabled={!signatureText || !signatureMeaning || !signaturePassword || !signatureRole || processingSignature}
            className="rounded-full bg-teal-600 hover:bg-teal-700 text-slate-900 dark:text-slate-100"
          >
            {processingSignature ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <PenLine className="w-4 h-4 mr-2" />
            )}
            Sign Document
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SignatureModal;
